
import { Component, Prop, Vue } from 'vue-property-decorator';
import CatalogSearchBar from '@/components/purchase-order/CatalogSearchBar.vue';
import CatalogProductTable from '@/components/purchase-order/CatalogProductTable.vue';
import PurchaseListSidebar from '@/components/purchase-order/PurchaseListSidebar.vue';
import { purchaseList } from '@/collections/purchase_list';
import type { PurchaseListItem } from '@/types/purchase-list';
import { SupplierModel } from '@/models/supplier';
import { ReportModel } from '@/models/report';
import { FilterColumnList } from '@/collections/filter_columns';

@Component({ components: { CatalogSearchBar, CatalogProductTable, PurchaseListSidebar } })
export default class PurchaseOrderCatalog extends Vue {
  @Prop({ required: true }) public readonly supplierId!: number;

  public supplier: SupplierModel | null = null;
  public supplierName = '';
  public currencyCode = 'USD';
  public products: any[] = [];
  public totalProducts = 0;
  public loading = false;
  public purchaseListRef = purchaseList;

  public async mounted(): Promise<void> {
    await this.loadSupplier();
    await Promise.all([
      this.loadProducts(),
      this.purchaseListRef.fetch(),
    ]);
  }

  public get listItemsByVariant(): Record<number, { id: number; quantity: number }> {
    const out: Record<number, { id: number; quantity: number }> = {};
    for (const group of Object.values(this.purchaseListRef.groups)) {
      for (const item of group.items) {
        out[item.variantId] = { id: item.id, quantity: Number(item.quantity) };
      }
    }
    return out;
  }

  public async loadSupplier(): Promise<void> {
    this.supplier = new SupplierModel({ id: Number(this.supplierId) });
    await this.supplier.fetch();
    this.supplierName = this.supplier.name || '';
    this.currencyCode = this.supplier.currency_code
      || this.supplier.options?.financial?.currency_code
      || 'USD';
  }

  public async loadProducts(): Promise<void> {
    if (!this.supplier) { return; }
    this.loading = true;
    try {
      const reportOptions: any = {
        initialFilterColumns: new FilterColumnList(),
        pageOptions: { page: 1, itemsPerPage: 50 },
        suggestFutureReorderQuantity: true,
      };
      const countOptions: any = {
        initialFilterColumns: new FilterColumnList(),
        suggestFutureReorderQuantity: true,
      };
      const [reportModel, countResponse]: [ReportModel, any] = await Promise.all([
        this.supplier.fetchProductsWithOptions('inventory_replenishment', 'eq', reportOptions),
        this.supplier.countProductsWithOptions('inventory_replenishment', 'eq', countOptions),
      ]);
      const rows = reportModel.getReportRowsData() || [];
      this.products = rows.map((r: any) => this.mapRow(r));
      this.totalProducts = countResponse?.totalItems || this.products.length;
    } catch (err) {
      this.products = [];
      this.totalProducts = 0;
      this.$emit('error', 'Failed to load products for supplier');
    } finally {
      this.loading = false;
    }
  }

  public mapRow(row: any): any {
    const unitCost = Number(row['[average_unit_cost]']) || Number(row['[unit_cost]']) || Number(row.unit_cost) || 0;
    const suggestedQuantity = Math.round(Number(row['[inventory_replenishment]'])) || 0;
    return {
      variantId: Number(row.variant_id),
      productName: row['Item[Item Description]'] || '',
      variant: row.variant || '',
      sku: row.product_sku || '',
      supplierSku: row.supplier_sku || row.supplierSku || '',
      variantImage: row.variant_image || '',
      currentStock: Number(row['[Actual Inventory Quantity]']) || 0,
      unitCost,
      suggestedQuantity,
      reorderDate: row['[reorder_date]'] || '',
      daysOfStock: row['[stock_days_inventory_planning]'] ?? null,
      incomingStock: row['[incoming_inventory_quantity]'] ?? null,
      historicalSales: row['[sold_quantity_param]'] ?? null,
    };
  }

  public async onQuickAdd(product: any): Promise<void> {
    await purchaseList.addItem({
      supplierId: this.supplierId,
      supplierName: this.supplierName,
      variantId: product.variantId,
      productName: product.productName,
      variant: product.variant,
      sku: product.sku,
      supplierSku: product.supplierSku,
      variantImage: product.variantImage,
      quantity: Number(product.suggestedQuantity) || 1,
      // US-CURR-013: the quick-add search bar is backed by
      // GET /supplier-products/supplier/:id/search, which returns SupplierProduct.cost
      // — already in the supplier's currency. 'supplier' is truthful here, and tells
      // the backend never to FX-convert this value.
      unitCost: Number(product.cost) || 0,
      costSource: 'supplier',
      currencyCode: this.currencyCode,
    });
    await this.refreshList();
  }

  public async onTableAdd(row: any): Promise<void> {
    const qty = Number(row.suggestedQuantity) || 1;
    await purchaseList.addItem({
      supplierId: this.supplierId,
      supplierName: this.supplierName,
      variantId: row.variantId,
      productName: row.productName,
      variant: row.variant,
      sku: row.sku,
      supplierSku: row.supplierSku,
      variantImage: row.variantImage,
      quantity: qty,
      // US-CURR-013: unlike quick-add, these rows come from mapRow() -> the
      // inventory_replenishment report's [average_unit_cost], which is in STORE
      // currency. Tag it 'shopify' so the backend resolves it (SupplierProduct.cost,
      // else FX-convert) instead of trusting it as a supplier-currency price.
      unitCost: Number(row.unitCost) || 0,
      costSource: 'shopify',
      currencyCode: this.currencyCode,
      metadata: { suggestedQuantity: row.suggestedQuantity, currentStock: row.currentStock },
    });
    await this.refreshList();
  }

  public async onTableStep({ row, delta }: { row: any; delta: number }): Promise<void> {
    const existing = this.listItemsByVariant[row.variantId];
    if (!existing) { return; }
    const newQty = existing.quantity + delta;
    if (newQty <= 0) {
      await purchaseList.removeItem(existing.id);
    } else {
      await purchaseList.updateItem(existing.id, { quantity: newQty });
    }
    await this.refreshList();
  }

  public async onQtyChange(item: PurchaseListItem): Promise<void> {
    if (Number(item.quantity) <= 0) {
      await purchaseList.removeItem(item.id);
    } else {
      await purchaseList.updateItem(item.id, { quantity: item.quantity });
    }
    await this.refreshList();
  }

  public async onRemove(item: PurchaseListItem): Promise<void> {
    await purchaseList.removeItem(item.id);
    await this.refreshList();
  }

  public async refreshList(): Promise<void> {
    await this.purchaseListRef.fetch();
  }
}
